import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSite } from "@/lib/useSite";
import { imgUrl, scrollToId } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  // 1. Destructure isLoading alongside data
  const { data, isLoading } = useSite();
  const s = data?.settings || {};

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const headline = s.hero_headline || "";
  const lines = headline.split("\n").filter(Boolean);
  const phone = s.phone || "";

  return (

          <section 
      ref={ref} 
      data-testid="hero-section" 
      className={`relative flex h-[100svh] min-h-[640px] w-full items-end overflow-hidden overflow-x-hidden bg-black text-white transition-opacity duration-500 ${
        isLoading ? "opacity-0" : "opacity-100"
      }`}
    >


      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={imgUrl(s.hero_image)}
          alt="A luxury roofing project completed with SRS materials"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-ink/45" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-ink/70 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-20 sm:px-10 sm:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="mb-7 flex items-center gap-3"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-srs" />
          <span data-testid="hero-badge" className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/75">
            {s.hero_badge || "SRS — Sun Roofing Solutions · Mandya"}
          </span>
        </motion.div>

               <h1
          data-testid="hero-headline"
          className="max-w-5xl font-display text-[clamp(2.75rem,7.5vw,6.5rem)] font-semibold leading-[1.0] tracking-[-0.03em]"
        >
          {lines.map((line, i) => (
            <span key={i} className="block w-full overflow-hidden pb-1">
             <motion.span
  className="block"
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 1.1, delay: 0.3 + i * 0.13, ease: EASE }}
>
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
          className="mt-7 max-w-lg text-base leading-relaxed text-white/75 sm:text-lg"
        >
          {s.hero_subhead || "Roofing, fencing, structural and agri solutions — premium materials, supplied on time, every time."}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4"
        >
          <button
            data-testid="hero-explore-cta"
            onClick={() => scrollToId("contact")}
            className="rounded-full bg-white px-8 py-4 text-sm font-semibold text-ink transition-colors hover:bg-srs hover:text-white"
          >
            Get a Free Estimate
          </button>
          <button
            data-testid="hero-enquire-cta"
            onClick={() => scrollToId("products")}
            className="text-sm font-medium text-white/85 underline decoration-white/30 underline-offset-8 transition-colors hover:text-white hover:decoration-white"
          >
            Explore the range
          </button>
        </motion.div>
      </div>
    </section>
  );
}
